import { HttpClient, registerCustomMutator, registerApiConfig } from "@charphq/api-client";
import { betterAuthTokenProvider } from "./better-auth-token-provider";
import type { AxiosRequestConfig, AxiosResponse } from "axios";
import { waitForEnvVar } from "./env";

/**
 * Web app specific API client instance that uses better-auth for token management
 * This replaces the default localStorage-based token provider with secure better-auth integration
 */
let webApiClient: HttpClient | null = null;

/**
 * Custom mutator function for Orval-generated API calls using better-auth
 * This will be registered globally so all generated hooks use it automatically
 */
let customInstance: (<T>(config: AxiosRequestConfig) => Promise<T>) | null = null;

/**
 * Initialize the API client configuration and instances.
 * This function waits for runtime environment variables (window.__ENV__) to be available
 * in production, but proceeds immediately in dev mode.
 *
 * Must be called before rendering React to ensure API calls use the correct configuration.
 */
export async function initializeApiClient(): Promise<void> {
  // Wait for environment variables to be available (with timeout)
  // In dev mode, this will return immediately since window.__ENV__ doesn't exist.
  // VITE_API_URL must be the bare backend origin WITHOUT a trailing /api — the orval-
  // generated hook paths already include the /api prefix (e.g. `/api/customers/`).
  const apiUrl = await waitForEnvVar("VITE_API_URL", "http://localhost:3001");

  // Register global API configuration for the web app
  registerApiConfig({
    baseURL: apiUrl,
    enableLogging: import.meta.env.DEV,
    timeout: 8000,
    retries: 1,
    withCredentials: true, // Enable cookies for better-auth session management
  });

  // Create the web app specific API client instance
  webApiClient = new HttpClient({
    authTokenProvider: betterAuthTokenProvider,
  });

  // Create custom mutator function
  customInstance = <T>(config: AxiosRequestConfig): Promise<T> => {
    if (!webApiClient) {
      throw new Error("API client not initialized. Call initializeApiClient() first.");
    }
    const instance = webApiClient.getInstance();
    return instance(config).then((response: AxiosResponse<T>) => response.data);
  };

  // Register our custom mutator globally
  // This ensures all generated API hooks use our better-auth integration
  registerCustomMutator(customInstance);
}

// Export the HTTP client instance for manual usage
// Note: This will be null until initializeApiClient() is called
export function getHttpClient(): HttpClient {
  if (!webApiClient) {
    throw new Error("API client not initialized. Call initializeApiClient() first.");
  }
  return webApiClient;
}

// Export for backward compatibility (but it will throw if not initialized)
export { getHttpClient as httpClient };
